import {Button, Col, Collapse, DatePicker, Form, message, Row, Select, Table, Tag} from "antd";
import React, {useEffect, useState} from "react";
import {DownloadOutlined, ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import {
    COMMON_DROPDOWN,
    NOT_AVAILABLE,
    PARAMETER_LIST,
    RECONCILIATION_STATUS,
    YYYY_MM_DD,
    YYYY_MM_DD_HH_MM
} from "../../../util/Constants";
import transactionFetchService from "../../../service/transactionFetchService";
import {
    MSG_FAILED_TO_FETCH_COMMON_DROPDOWN_TYPES_ERROR,
    MSG_FAILED_TO_FETCH_UPLOADED_EXCEL_LIST_ERROR
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import {ReconciliationTableDataDto} from "../../../dto/admin/ReconciliationTableDataDto";
import {BankUploadDataFilterDto} from "../../../dto/admin/BankUploadDataFilterDto";
import {InputDropdownParameterDto} from "../../../dto/InputDropdownParameterDto";
import customerService from "../../../service/customerService";
import {DropdownListDto} from "../../../dto/DropdownListDto";
import {saveAs} from 'file-saver';
import ExcelJS from 'exceljs';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileExcel} from "@fortawesome/free-solid-svg-icons";

const {Panel} = Collapse;
const {RangePicker} = DatePicker;

const InvestReconciliationExcelView = () => {
    const [reconciledExcelDataList, setReconciledExcelDataList] = useState<ReconciliationTableDataDto[]>([]);
    const [isTableLoading, setTableLoading] = useState(false);
    const [fundDropdownList, setFundDropdownList] = useState<DropdownListDto[]>([]);
    const [collectionBankDropdownList, setCollectionBankDropdownList] = useState<DropdownListDto[]>([]);
    const [form] = Form.useForm();

    const toFixed = (value: string | undefined): any => {
        if (undefined != value) {
            let val = value.toString();
            if (val.indexOf(".") != -1) {
                val = val.slice(0, (val.indexOf(".")) + 3);
            }
            val = (+val).toFixed(2);
            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return "0.00";
    }

    const columns = [

        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            sorter: (a: any, b: any) => {
                a = a.date == null ? '' : a.date
                b = b.date == null ? '' : b.date
                return a.localeCompare(b);
            },
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Portfolio ID',
            dataIndex: 'portFolioId',
            sorter: (a: any, b: any) => {
                a = a.portFolioId == null ? '' : a.portFolioId
                b = b.portFolioId == null ? '' : b.portFolioId
                return a.localeCompare(b);
            },
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Fund',
            dataIndex: 'fund',
            sorter: (a: any, b: any) => {
                a = a.fund == null ? '' : a.fund
                b = b.fund == null ? '' : b.fund
                return a.localeCompare(b);
            },
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Amount',
            dataIndex: 'value',
            align: 'right',
            sorter: (a: any, b: any) => a.value - b.value,
            render: (text: any, rec: any, index: number) => {
                return (<span>{toFixed(text)}</span>)
            }

        },
        {
            title: 'Collection Bank',
            dataIndex: 'collectionBank',
            sorter: (a: any, b: any) => {
                a = a.collectionBank == null ? '' : a.collectionBank
                b = b.collectionBank == null ? '' : b.collectionBank
                return a.localeCompare(b);
            },
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Ref No.',
            dataIndex: 'nic',
            sorter: (a: any, b: any) => {
                a = a.nic == null ? '' : a.nic
                b = b.nic == null ? '' : b.nic
                return a.localeCompare(b);
            },
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Uploaded Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            sorter: (a: any, b: any) => {
                a = a.createdDate == null ? '' : a.createdDate
                b = b.createdDate == null ? '' : b.createdDate
                return a.localeCompare(b);
            },
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD_HH_MM) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Reconciled By',
            dataIndex: 'authAdmin',
            sorter: (a: any, b: any) => {
                a = a.authAdmin == null ? '' : a.authAdmin
                b = b.authAdmin == null ? '' : b.authAdmin
                return a.localeCompare(b);
            },
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.status == null ? '' : a.status
                b = b.status == null ? '' : b.status
                return a.localeCompare(b);
            },
            render: (text: any, rec: any, index: number) => {
                return (<span>{getTag(text)}</span>)
            }
        }
    ];

    const getTag = (status: string) => {
        switch (status) {
            case RECONCILIATION_STATUS.AUTO_RECONCILE: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#7cb305' key={status}>
                        {replaceUnderscore(status)}
                    </Tag>
                )
            }
            case RECONCILIATION_STATUS.MANUAL_RECONCILE: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#1d39c4' key={status}>
                        {replaceUnderscore(status)}
                    </Tag>
                )
            }
            case RECONCILIATION_STATUS.REJECT: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#fa541c' key={status}>
                        {replaceUnderscore(status)}
                    </Tag>
                )
            }
            case RECONCILIATION_STATUS.RECONCILE_INPROGRESS: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#d4b106' key={status}>
                        {replaceUnderscore(status)}
                    </Tag>
                )
            }
            case RECONCILIATION_STATUS.SUSPEND: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#8c8c8c' key={"SUSPENSE"}>
                        {"SUSPENSE"}
                    </Tag>
                )
            }
        }
    }

    const replaceUnderscore = (text: string) => {
        return text.toUpperCase().replaceAll('_', ' ')
    }
    useEffect(() => {
        const yesterday = dayjs().subtract(1, 'day');
        const today = dayjs();
        form.setFieldsValue({
            createdDate:[yesterday, today]
        })
        loadCommonDropdownDataList()
    }, []);
    const fetchManualReconciliationList = (filterDto: BankUploadDataFilterDto) => {
        setTableLoading(true)
        transactionFetchService.getManualReconciledExcelDetails(filterDto).then(res => {
            if (res.data !== null) {
                setReconciledExcelDataList(res.data.results)
                setTableLoading(false)
            } else {
                message.error(MSG_FAILED_TO_FETCH_UPLOADED_EXCEL_LIST_ERROR).then();
                setTableLoading(false)
            }

        }, (reason) => {
            ErrorHandler.handleError(reason);
            setTableLoading(false)

        })
    }


    const loadCommonDropdownDataList = () => {

        const inputDropdownParameterDto: InputDropdownParameterDto[] = [
            {
                dropdownDataSourceId: COMMON_DROPDOWN.FUND_LIST,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            },
            {
                dropdownDataSourceId: COMMON_DROPDOWN.COLLECTION_BANK_LIST,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            }
        ]
        customerService.getCommonDropdownList(inputDropdownParameterDto).then(res => {
            if (res.data !== null) {

                let dropdownDataList = res.data.results.dropdownDataList;
                setFundDropdownList(dropdownDataList[COMMON_DROPDOWN.FUND_LIST])
                setCollectionBankDropdownList(dropdownDataList[COMMON_DROPDOWN.COLLECTION_BANK_LIST])

            } else {
                message.error(MSG_FAILED_TO_FETCH_COMMON_DROPDOWN_TYPES_ERROR).then();
            }
        }, (reason) => {
            ErrorHandler.handleError(reason);
        })
    }

    const handleDateChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
        if (dates && dates[0]) {
            const startDate = dates[0];
            const endDate = startDate.add(7, 'day');
            form.setFieldsValue({
                createdDate: [startDate, endDate],
            });
        }
    };

    // Reset to default range on button click
    const resetDates = () => {
        const yesterday = dayjs().subtract(1, 'day');
        const today = dayjs();
        form.setFieldsValue({
            createdDate:[yesterday, today]
        })
    };


    const onSearch = (values: any) => {


        if (checkCanFilter(values)) {
            let uploadedDateFrom = null
            let uploadedDateTo = null
            if (values.createdDate) {
                uploadedDateFrom = values.createdDate[0].format(YYYY_MM_DD)
                uploadedDateTo = values.createdDate[1].format(YYYY_MM_DD)
            }

            let bankExcelDto = {
                uploadDateFrom: uploadedDateFrom,
                uploadDateTo: uploadedDateTo,
                fund: values.fund,
                collectionBank: values.collectionBank,
                status: values.status
            } as BankUploadDataFilterDto

            fetchManualReconciliationList(bankExcelDto)
        } else {
            message.warning("Please fill filter criteria before search!")
        }
    }

    const checkCanFilter = (values: any) => {
        if (null != values.fund && "" != values.fund) {
            return true;
        } else if (null != values.collectionBank && "" != values.collectionBank) {
            return true;
        } else if (null != values.status && "" != values.status) {
            return true;
        } else if (null != values.createdDate && "" != values.createdDate) {
            return true;
        } else {
            return false;
        }
    }
    const onReset = () => {
        form.resetFields()
        setReconciledExcelDataList([])
        resetDates();
    }

    // const exportToExcel = () => {
    //     setTableLoading(true);
    //
    //     const exportData = reconciledExcelDataList.map(item => ({
    //         'Date': item.date,
    //         'Portfolio ID': item.portFolioId,
    //         'Fund': item.fund,
    //         'Amount': toFixed(item.value),
    //         'Collection Bank': item.collectionBank,
    //         'Ref No.': item.nic,
    //         'Uploaded Date': item.createdDate,
    //         'Reconciled By': item.authAdmin,
    //         'Status': replaceUnderscore(item.status as string),
    //     }));
    //
    //     setTimeout(() => {
    //         const worksheet = XLSX.utils.json_to_sheet(exportData);
    //
    //         // Define the table range
    //         const tableRange = `A1:I${exportData.length + 1}`;
    //         worksheet['!ref'] = tableRange;
    //
    //         // Define column widths
    //         worksheet['!cols'] = [
    //             { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 25 }
    //         ];
    //
    //         // Add cell styles for the "Amount" column and borders
    //         const borderStyle = {
    //             top: { style: 'thin', color: { rgb: '000000' } },
    //             left: { style: 'thin', color: { rgb: '000000' } },
    //             bottom: { style: 'thin', color: { rgb: '000000' } },
    //             right: { style: 'thin', color: { rgb: '000000' } },
    //         };
    //
    //         const alignmentRight = { horizontal: 'right' };
    //
    //         // Loop through all rows and apply borders and alignment
    //         const range = XLSX.utils.decode_range(worksheet['!ref']);
    //         for (let R = range.s.r; R <= range.e.r; ++R) {
    //             for (let C = range.s.c; C <= range.e.c; ++C) {
    //                 const cellAddress = { c: C, r: R };
    //                 const cellRef = XLSX.utils.encode_cell(cellAddress);
    //                 if (!worksheet[cellRef]) worksheet[cellRef] = {};
    //                 worksheet[cellRef].s = {
    //                     ...(worksheet[cellRef].s || {}),
    //                     border: borderStyle,
    //                     alignment: C === 3 ? alignmentRight : {} // Apply right alignment to the 4th column (Amount)
    //                 };
    //             }
    //         }
    //
    //         // Create workbook and append worksheet
    //         const workbook = XLSX.utils.book_new();
    //         XLSX.utils.book_append_sheet(workbook, worksheet, 'Reconciliation Excel');
    //
    //         // Write to Excel and trigger download
    //         const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    //         const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    //         saveAs(blob, 'ReconciliationExcel.xlsx');
    //
    //         setTableLoading(false);
    //     }, 1500);
    // };

    const exportToExcel = () => {

        if (reconciledExcelDataList.length <= 0) {
            message.warning("No available data for download!")
        } else {

            setTableLoading(true);

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Reconciliation Excel');

            worksheet.columns = [
                {header: 'Date', key: 'date', width: 15},
                {header: 'Portfolio ID', key: 'portfolioId', width: 15},
                {header: 'Fund', key: 'fund', width: 25},
                {header: 'Amount', key: 'amount', width: 15, style: {alignment: {horizontal: 'right'}}}, // Right align Amount
                {header: 'Collection Bank', key: 'collectionBank', width: 25},
                {header: 'Ref No.', key: 'refNo', width: 25},
                {header: 'Uploaded Date', key: 'uploadedDate', width: 25},
                {header: 'Reconciled By', key: 'reconciledBy', width: 25},
                {header: 'Status', key: 'status', width: 25},
            ];

            reconciledExcelDataList.forEach(item => {
                worksheet.addRow({
                    date: item.date || '-',
                    portfolioId: item.portFolioId || '-',
                    fund: item.fund || '-',
                    amount: toFixed(item.value) || '-',
                    collectionBank: item.collectionBank || '-',
                    refNo: item.nic || '-',
                    uploadedDate: item.createdDate || '-',
                    reconciledBy: item.authAdmin || '-',
                    status: replaceUnderscore(item.status as string) || '-',
                });
            });

            const headerRow = worksheet.getRow(1);
            headerRow.eachCell({includeEmpty: true}, (cell) => {
                cell.font = {bold: true};
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: {argb: 'd3d3d3'}
                };
            });
            worksheet.eachRow((row, rowIndex) => {
                row.eachCell((cell, colNumber) => {
                    cell.border = {
                        top: {style: 'thin', color: {argb: '000000'}},
                        left: {style: 'thin', color: {argb: '000000'}},
                        bottom: {style: 'thin', color: {argb: '000000'}},
                        right: {style: 'thin', color: {argb: '000000'}}
                    };

                    if (colNumber === 4) {
                        cell.alignment = {horizontal: 'right'};
                    }
                });
            });

            workbook.xlsx.writeBuffer().then((buffer) => {
                const blob = new Blob([buffer], {type: 'application/octet-stream'});
                saveAs(blob, 'ReconciliationExcel.xlsx');
                setTableLoading(false);
            });

        }
    };

    return (
        <div className={'invest-recon-auth-page'}>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24} className='page-header-main-title-col'>
                    <h2 className='page-header-main-title'>
                        <div className='header_icon header-icon-center'>
                            <FontAwesomeIcon icon={faFileExcel}/>
                        </div>
                        Invest Reconciliation Uploaded Excels
                    </h2>
                </Col>
            </Row>

            <Row gutter={[16, 16]} className='margin_top_30'>
                <Col span={24}>
                    <Collapse>
                        <Panel header="Search Filters" key="1">
                            <Form form={form} layout={"vertical"} onFinish={onSearch}>
                                <Row gutter={[16, 16]}>

                                    <Col span={6}>
                                        <Form.Item
                                            label="Fund" name={"fund"}>
                                            <Select placeholder={"Select Fund"}
                                                    allowClear
                                                    options={fundDropdownList?.map((fund) => ({
                                                        value: fund.label,
                                                        label: fund.label,
                                                    }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item
                                            label="Collection Bank" name={"collectionBank"}>
                                            <Select placeholder={"Select Collection Bank"}
                                                    allowClear
                                                    options={collectionBankDropdownList?.map((bank) => ({
                                                        value: bank.label,
                                                        label: bank.label,
                                                    }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item
                                            label="Status" name={"status"}>
                                            <Select placeholder={"Select Status"}
                                                    allowClear
                                                    options={[
                                                        {
                                                            value: RECONCILIATION_STATUS.AUTO_RECONCILE,
                                                            label: replaceUnderscore(RECONCILIATION_STATUS.AUTO_RECONCILE)
                                                        },
                                                        {
                                                            value: RECONCILIATION_STATUS.MANUAL_RECONCILE,
                                                            label: replaceUnderscore(RECONCILIATION_STATUS.MANUAL_RECONCILE)
                                                        },
                                                        {
                                                            value: RECONCILIATION_STATUS.REJECT,
                                                            label: replaceUnderscore(RECONCILIATION_STATUS.REJECT)
                                                        },
                                                        {
                                                            value: RECONCILIATION_STATUS.SUSPEND,
                                                            label: "SUSPENSE"
                                                        },
                                                        {
                                                            value: RECONCILIATION_STATUS.RECONCILE_INPROGRESS,
                                                            label: replaceUnderscore(RECONCILIATION_STATUS.RECONCILE_INPROGRESS)
                                                        },
                                                    ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item label="Uploaded Date" name="createdDate">
                                            <RangePicker placeholder={["From Date", "To Date"]}
                                                         onChange={handleDateChange}
                                                         allowClear={false}
                                                         style={{width: '100%'}}/>

                                        </Form.Item>
                                    </Col>

                                </Row>
                                <Row gutter={[16, 0]}>
                                    <Col span={24}>
                                        <div className="tr-filter-btns">
                                            <Button className={'tr-reset-btn'} icon={<ReloadOutlined/>}
                                                    onClick={onReset}>
                                                Reset
                                            </Button>
                                            <Button className={'tr-search-btn'} type="primary"
                                                    htmlType={'submit'}
                                                    icon={<SearchOutlined/>}>
                                                Search
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </Panel>
                    </Collapse>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Table scroll={{x: 'max-content'}} columns={columns} dataSource={reconciledExcelDataList}
                           loading={isTableLoading}/>

                </Col>


            </Row>
            <Row gutter={[16, 20]} className='margin_top_20'>
                <Col span={24} className={'recon-btn-row'} style={{borderTop: 'none'}}>
                    <Button type="primary" size={'large'} onClick={exportToExcel} style={{width: 200}}
                            loading={isTableLoading}
                            icon={<DownloadOutlined/>}>
                        Download as Excel
                    </Button>
                </Col>
            </Row>
        </div>


    )

}
export default InvestReconciliationExcelView;

